import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { Link, Stack } from "@chakra-ui/react";
import { type FC } from "react";
import {
  confirmMessageBoxTestId,
  titleTestId,
  textToyNumberTestId,
  textShipmentPeriodTestId,
  textShipmentDeadlineTestId,
  textPaymentPeriodTestId,
  textContactTestId,
} from "./const";
import { type PlanData } from "@/components/fragments/argyle/plan-data-table";
import { isOneTimePaymentStandard, isSmile, isPutit } from "@/modules/utils/subscription-info";
import { isNonNullable } from "@/modules/utils/predicate";
import { TextButton } from "@/components/essentials/button";
import { toysubApplicationFormContactUrl } from "@/configs/externalUrls";

export interface ConfirmMessageBoxProps {
  readonly planData?: PlanData;
  readonly promotionCode?: string | undefined;
}

export const ConfirmMessageBox: FC<ConfirmMessageBoxProps> = ({ planData }) => {
  return (
    <Box background="background.main" borderRadius="0.5rem" p="4" data-testid={confirmMessageBoxTestId}>
      <Typography variant="display5" color="theme.yellow.main" data-testid={titleTestId}>
        必ずご確認ください
      </Typography>
      <Stack spacing="3" mt="4">
        {isNonNullable(planData) &&
          (isSmile(planData.id) ? (
            // スマイル
            <>
              <Typography variant="sentence2" data-testid={textToyNumberTestId}>
                おもちゃの点数は4点です。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentPeriodTestId}>
                おもちゃの交換は基本的に
                <Typography as="span" variant="sentence2-bold">
                  60日に1回
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" mb="1rem">
                ※お客さまのリクエストへの対応を行った場合などには、
                <Typography as="span" variant="sentence2-bold">
                  67日ほどかかる
                </Typography>
                ことがございます。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentDeadlineTestId}>
                商品は
                <Typography as="span" variant="sentence2-bold">
                  お申し込みから6営業日以内
                </Typography>
                にご指定のお届け先に発送します。
              </Typography>
              <Typography variant="sentence2" data-testid={textPaymentPeriodTestId}>
                <Typography as="span" variant="sentence2-bold">
                  商品発送
                </Typography>
                後に請求開始、以後
                <Typography as="span" variant="sentence2-bold">
                  契約期間毎のご請求
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" data-testid={textContactTestId}>
                返品・ご解約の際は、
                <TextButton
                  href={toysubApplicationFormContactUrl}
                  as={Link}
                  rel="noopener norefferer"
                  target="_blank"
                  color="mono.dark.54"
                  size="sm"
                >
                  <Typography as="span" variant="sentence2">
                    お問合せフォーム
                  </Typography>
                </TextButton>
                よりご連絡ください。
              </Typography>
              <Typography variant="sentence2">
                返品による契約解除は、お客さまによる送料ご負担の上、未開封の場合のみ可能です。
              </Typography>
              <Typography variant="sentence2">
                解約申請は随時受付しております。解約が可能となるのはお客さまのおもちゃ交換サイクルに応じて設定される2回目の発送が可能となる日以降となります。
              </Typography>
            </>
          ) : isPutit(planData.id) ? (
            // プチ
            <>
              <Typography variant="sentence2" data-testid={textToyNumberTestId}>
                おもちゃの点数は2点です。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentPeriodTestId}>
                おもちゃの交換は基本的に
                <Typography as="span" variant="sentence2-bold">
                  60日に1回
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" mb="1rem">
                ※お客さまのリクエストへの対応を行った場合などには、
                <Typography as="span" variant="sentence2-bold">
                  67日ほどかかる
                </Typography>
                ことがございます。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentDeadlineTestId}>
                商品は
                <Typography as="span" variant="sentence2-bold">
                  お申し込みから6営業日以内
                </Typography>
                にご指定のお届け先に発送します。
              </Typography>
              <Typography variant="sentence2" data-testid={textPaymentPeriodTestId}>
                <Typography as="span" variant="sentence2-bold">
                  商品発送
                </Typography>
                後に請求開始、以後
                <Typography as="span" variant="sentence2-bold">
                  契約期間毎のご請求
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" data-testid={textContactTestId}>
                返品・ご解約の際は、
                <TextButton
                  href={toysubApplicationFormContactUrl}
                  as={Link}
                  rel="noopener norefferer"
                  target="_blank"
                  color="mono.dark.54"
                  size="sm"
                >
                  <Typography as="span" variant="sentence2">
                    お問合せフォーム
                  </Typography>
                </TextButton>
                よりご連絡ください。
              </Typography>
              <Typography variant="sentence2">
                返品による契約解除は、お客さまによる送料ご負担の上、未開封の場合のみ可能です。
              </Typography>
              <Typography variant="sentence2">
                解約申請は随時受付しております。解約が可能となるのはお客さまのおもちゃ交換サイクルに応じて設定される2回目の発送が可能となる日以降となります。
              </Typography>
            </>
          ) : isOneTimePaymentStandard(planData.id) ? (
            // 一括払い
            <>
              <Typography variant="sentence2" data-testid={textToyNumberTestId}>
                おもちゃの点数は6点です。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentPeriodTestId}>
                おもちゃの交換は基本的に
                <Typography as="span" variant="sentence2-bold">
                  60日に1回
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentPeriodTestId} mb="1rem">
                ※お客さまのリクエストへの対応を行った場合などには、
                <Typography as="span" variant="sentence2-bold">
                  67日ほどかかる
                </Typography>
                ことがございます。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentDeadlineTestId}>
                商品は
                <Typography as="span" variant="sentence2-bold">
                  お申し込みから6営業日以内
                </Typography>
                にご指定のお届け先に発送します。
              </Typography>
              <Typography variant="sentence2" data-testid={textPaymentPeriodTestId}>
                <Typography as="span" variant="sentence2-bold">
                  商品発送
                </Typography>
                後に請求開始、以後
                <Typography as="span" variant="sentence2-bold">
                  契約期間毎のご請求
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" data-testid={textContactTestId}>
                返品・ご解約の際は、
                <TextButton
                  href={toysubApplicationFormContactUrl}
                  as={Link}
                  rel="noopener norefferer"
                  target="_blank"
                  color="mono.dark.54"
                  size="sm"
                >
                  <Typography as="span" variant="sentence2">
                    お問合せフォーム
                  </Typography>
                </TextButton>
                よりご連絡ください。
              </Typography>
              <Typography variant="sentence2">
                返品による契約解除は、お客さまによる送料ご負担の上、未開封の場合のみ可能です。
              </Typography>
              <Typography variant="sentence2">
                解約申請は随時受付しております。解約が可能となるのはお客さまのおもちゃ交換サイクルに応じて設定される2回目の発送が可能となる日以降となります。
              </Typography>

              <Typography variant="sentence2">
                一括払いの場合には以下の返金の計算式のとおり返金手数料を差し引いて返金します。本サービスを利用した期間を毎月支払いの月額料金で再計算し、支払済み額との差額について、返金金手数料を差し引いて返金します。
              </Typography>
              <Typography variant="sentence2">
                返金額＝支払済み金額―(毎月支払いの金額[※１]×利用月数[※２])―返金手数料770円
              </Typography>
            </>
          ) : (
            // スタンダード
            <>
              <Typography variant="sentence2" data-testid={textToyNumberTestId}>
                おもちゃの点数は6点です。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentPeriodTestId}>
                おもちゃの交換は基本的に
                <Typography as="span" variant="sentence2-bold">
                  60日に1回
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" mb="1rem">
                ※お客さまのリクエストへの対応を行った場合などには、
                <Typography as="span" variant="sentence2-bold">
                  67日ほどかかる
                </Typography>
                ことがございます。
              </Typography>
              <Typography variant="sentence2" data-testid={textShipmentDeadlineTestId}>
                商品は
                <Typography as="span" variant="sentence2-bold">
                  お申し込みから6営業日以内
                </Typography>
                にご指定のお届け先に発送します。
              </Typography>
              <Typography variant="sentence2" data-testid={textPaymentPeriodTestId}>
                <Typography as="span" variant="sentence2-bold">
                  商品発送
                </Typography>
                後に請求開始、以後
                <Typography as="span" variant="sentence2-bold">
                  契約期間毎のご請求
                </Typography>
                となります。
              </Typography>
              <Typography variant="sentence2" data-testid={textContactTestId}>
                返品・ご解約の際は、
                <TextButton
                  href={toysubApplicationFormContactUrl}
                  as={Link}
                  rel="noopener norefferer"
                  target="_blank"
                  color="mono.dark.54"
                  size="sm"
                >
                  <Typography as="span" variant="sentence2">
                    お問合せフォーム
                  </Typography>
                </TextButton>
                よりご連絡ください。
              </Typography>
              <Typography variant="sentence2">
                返品による契約解除は、お客さまによる送料ご負担の上、未開封の場合のみ可能です。
              </Typography>
              <Typography variant="sentence2">
                解約申請は随時受付しております。解約が可能となるのはお客さまのおもちゃ交換サイクルに応じて設定される2回目の発送が可能となる日以降となります。
              </Typography>
            </>
          ))}
        <div id="campaign-confirm-announce-area" />
      </Stack>
    </Box>
  );
};
